import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { BookingStatus } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { useLocation } from "wouter";
import { LoaderCircle } from "lucide-react";
import { Sidebar } from "@/components/layout/Sidebar";
import { User, Home, Calendar, LogOut } from "lucide-react";

export default function BookingConfirmation() {
  const [_, params] = useRoute('/bookings/:id/confirmation');
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [property, setProperty] = useState<any>(null);

  // Guest menu items for sidebar
  const guestMenu = [
    { label: "My Bookings", href: "/bookings", icon: <Calendar /> },
    { label: "Profile", href: "/profile", icon: <User /> },
    { label: "Home", href: "/", icon: <Home /> },
    { label: "Logout", href: "/logout", icon: <LogOut /> },
  ];

  useEffect(() => {
    const confirmBooking = async () => {
      if (!params?.id) return;

      try {
        // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentId = urlParams.get('payment_intent');
        const redirectStatus = urlParams.get('redirect_status');

        if (redirectStatus === 'succeeded' && paymentIntentId) {
          // Update booking status
          const bookingResponse = await apiRequest(
            "PUT",
            `bookings/${params.id}/status`,
            {
              status: BookingStatus.CONFIRMED,
              stripePaymentIntentId: paymentIntentId
            }
          );

          setBooking(bookingResponse);

          // Get property details
          const propertyResponse = await apiRequest(
            "GET",
            `properties/${bookingResponse.propertyId}`
          );

          setProperty(propertyResponse);

          toast({
            title: "Booking Confirmed",
            description: "Your booking has been successfully confirmed!",
          });
        } else {
          // If no payment intent, try to get the booking directly
          const bookingResponse = await apiRequest(
            "GET",
            `bookings/${params.id}`
          );

          if (bookingResponse.status === BookingStatus.CONFIRMED) {
            setBooking(bookingResponse);
            
            // Get property details
            const propertyResponse = await apiRequest(
              "GET",
              `properties/${bookingResponse.propertyId}`
            );

            setProperty(propertyResponse);
          } else {
            toast({
              title: "Payment Failed",
              description: "There was an issue with your payment. Please try again.",
              variant: "destructive",
            });
            setLocation('/bookings');
          }
        }
      } catch (error) {
        console.error("Error confirming booking:", error);
        toast({
          title: "Error",
          description: "Failed to confirm booking. Please contact support.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    confirmBooking();
  }, [params?.id, toast, setLocation]);

  if (loading) {
    return (
      <div className="flex">
        <Sidebar navItems={guestMenu} />
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <LoaderCircle className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (!booking || !property) {
    return (
      <div className="flex">
        <Sidebar navItems={guestMenu} />
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Booking Not Found</CardTitle>
              <CardDescription>The requested booking could not be found.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={() => setLocation('/bookings')} className="w-full">
                View My Bookings
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar navItems={guestMenu} />
      <div className="flex-1">
        <div className="container max-w-2xl py-8">
          <Card>
            <CardHeader>
              <CardTitle>Booking Confirmed!</CardTitle>
              <CardDescription>
                Your booking at {property.title} has been successfully confirmed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-1">Check-in</h3>
                  <p className="text-muted-foreground">
                    {format(new Date(booking.checkIn), "PPP")}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Check-out</h3>
                  <p className="text-muted-foreground">
                    {format(new Date(booking.checkOut), "PPP")}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Guests</h3>
                  <p className="text-muted-foreground">
                    {booking.guestCount} {booking.guestCount === 1 ? 'guest' : 'guests'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Total Amount</h3>
                  <p className="text-muted-foreground">
                    AED {booking.totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <h3 className="font-semibold mb-2">Property Details</h3>
                <p className="text-muted-foreground">
                  {property.address}
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex gap-4">
              <a
                href={`/properties/${property.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button className="w-full">View Property</Button>
              </a>
              <Button onClick={() => setLocation('/bookings')} className="flex-1" variant="outline">
                View All Bookings
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
} 